import { useEffect, useState } from "react";

// Ported from ai-medical-consultation-assistant/src/components/StructuredView.jsx
// `hideHeader` drops the "Observations" title + "Add all" (the host panel
// already provides a "Suggestions" header) and the card chrome.
//
// Each suggestion is shown directly in an editable field — the doctor edits the
// wording in place (no separate editor step) and clicks "+ Add". Once added the
// button turns green ("Added ✓") but stays enabled, so the same suggestion can
// be inserted into the examination several times.
//
// The full AI-revised transcript is appended at the bottom as one more editable
// suggestion.
export default function StructuredView({
  propositions,
  onEditItem,
  onAddItem,
  onAddAll,
  revisedTranscript = "",
  onAddTranscript,
  hideHeader = false,
}) {
  const items = propositions.observations ?? [];

  const list =
    items.length === 0 ? (
      <p className="proposition-card__empty">—</p>
    ) : (
      <ul className="editable-list">
        {items.map((item) => (
          <li key={item.id} className="editable-row">
            <input
              className="editable-input"
              value={item.text}
              onChange={(e) => onEditItem("observations", item.id, e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  onAddItem("observations", item);
                }
              }}
            />
            <button
              type="button"
              className={`chip-button ${item.added ? "chip-button--added" : ""}`}
              onClick={() => onAddItem("observations", item)}
              title={
                item.added
                  ? "Déjà ajouté — cliquez pour l'ajouter de nouveau"
                  : "Ajouter à l'examen"
              }
            >
              {item.added ? "Added ✓" : "+ Add"}
            </button>
          </li>
        ))}
      </ul>
    );

  const transcriptRow = (
    <TranscriptSuggestion text={revisedTranscript} onAdd={onAddTranscript} />
  );

  if (hideHeader) {
    return (
      <div className="structured-view">
        {list}
        {transcriptRow}
      </div>
    );
  }

  return (
    <div className="structured-view">
      <div className="proposition-card">
        <div className="proposition-card__header">
          <h3>Observations</h3>
          <button
            type="button"
            className="chip-button"
            disabled={items.length === 0}
            onClick={() => onAddAll("observations")}
          >
            Add all{items.length > 0 ? ` (${items.length})` : ""}
          </button>
        </div>
        {list}
      </div>
      {transcriptRow}
    </div>
  );
}

/**
 * Bottom suggestion: the complete AI-revised transcript. Seeded from `text` and
 * kept in sync with it while the doctor has not touched it; once edited it stays
 * as typed. "+ Add" inserts the (edited) text into the examination editor and
 * can be used several times.
 */
function TranscriptSuggestion({ text, onAdd }) {
  const [draft, setDraft] = useState(text);
  const [dirty, setDirty] = useState(false);
  const [added, setAdded] = useState(false);

  useEffect(() => {
    if (!dirty) setDraft(text);
  }, [text, dirty]);

  if (!text) return null;

  const handleAdd = () => {
    const value = draft.trim();
    if (!value) return;
    onAdd?.(value);
    setAdded(true);
  };

  return (
    <div className="transcript-suggestion">
      <div className="transcript-suggestion__header">
        <h3>Transcript</h3>
        <button
          type="button"
          className={`chip-button ${added ? "chip-button--added" : ""}`}
          onClick={handleAdd}
          title={
            added
              ? "Déjà ajouté — cliquez pour l'ajouter de nouveau"
              : "Ajouter à l'examen"
          }
        >
          {added ? "Added ✓" : "+ Add"}
        </button>
      </div>
      <textarea
        className="transcript-suggestion__text"
        rows={4}
        value={draft}
        onChange={(e) => {
          setDirty(true);
          setDraft(e.target.value);
        }}
      />
    </div>
  );
}
